/**
 * Tracker
 * @module Tracker
 *
 * Tracker is a general purpose function for loading, initializing, and proxying
 * track. Tracker relies on the passed in arguments to determine how to load,
 * initialize, filter, and track to a particular service. The tracking proxy
 * will log calls to the tracking function for local and development environments
 *
 * @param {Object} options The configurable options object for loading
 *
 * Supported options:
 * {Array}    conf        A configuration array containing individual
 *                        configuration elements
 * {function} library     A function that once when will load any libraries that
 *                        the tracker requires
 * {function} initialize  A function that will be called once for each valid
 *                        configuration object in the trackerConf array
 * {function} track       A function that will be called by the tracking proxy
 *                        with the arguments that were passed in to returned
 *                        tracker
 * {function} [filter]    An optional function that is used to filter the
 *                        tracking configurations to track to based on the
 *                        proxied tracking request
 */
export default function tracker(options) {

  // Make sure that the required arguments are passed in
  if (!options.conf ||
      !options.library ||
      !options.initialize ||
      !options.track) {
    return function() {};
  }

  var trackerConf = options.conf,
      libFn = options.library,
      initFn = options.initialize,
      trackFn = options.track,
      filterFn = options.filter,
      debugName = options.name || 'Tracker';

  // Execute the code required to load the tracking library
  libFn();

  /**
   * Handle the environment based setup of the tracker based on the
   * available configurations
   */
  return (function trackerStartup() {

    /**
     * Tracks a tracker request to each of the elements that are defined in
     * its environment configuration. Uses the optional filter object and
     * passed in filter function to determine the actions to track
     *
     * @param trackingArgs array An array of arguments that represents the
     *                           request to track
     */
    var track = function track(trackingArgs) {

      // Generate a unique id for this tracking call
      var trackId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random()*16|0, v = c == 'x' ? r : (r&0x3|0x8);
          return v.toString(16);
      });

      // For each entry in the tracker list, track to it if appropriate
      for (var i = trackerConf.length - 1; i >= 0; i--) {

        // If a tracking type whitelist is defined, make sure that this
        // tracking request is in it before tracking
        if (trackerConf[i].filter &&
            trackerConf[i].filter.length > 0 &&
            typeof filterFn !== 'undefined') {

          // This tracking code has a filter specified, check to see if the
          // tracking request is valid for this code
          if (!filterFn(trackerConf[i].filter, trackingArgs)) {
            continue;
          }
        }

        // If this a local or development build then wrap the tracker global
        // function with a function that logs all calls to the console before
        // sending them to the tracker endpoint
        if (process.env.NODE_ENV === 'local' || process.env.NODE_ENV === 'development') {

          // Log the call to console
          if (typeof console !== 'undefined' && typeof console.log !== 'undefined') {
            console.log(debugName + ' called: ' + trackingArgs.slice().map(function (arg) {
              return typeof arg === 'function' ? arg.toString() : JSON.stringify(arg);
            }) + ' for: ' + JSON.stringify(trackerConf[i]));
          }
        } else {

          // Now that the track has been validated, perform the provided
          // tracking function
          (function(id, args, conf) {
            trackFn(id, args, conf);
          })(trackId, trackingArgs.slice(), trackerConf[i]);
        }
      }
    };

    // Define a tracking function container that holds holds the environment
    // specific tracking function
    var trackProxy = function () {
      track.apply(window, [[].slice.call(arguments)]);
    };

    /**
     * Initializes a tracker based on a set of tracking options
     *
     * @param trackingOptions object An object of tracking options that are
     *                               used to initialize with Facebook
     */
    var initTrackingCode = function initTrackingCode(trackingOptions) {

      // A tracking code must be present in the options, if it is not
      // fail out of initialization
      if (typeof trackingOptions === 'undefined') {
        return;
      }

      // Initialize the tracker for the passed in options
      initFn(trackingOptions);
    };

    // For each entry in the tracker list, run the tracker initialization
    for (var i = trackerConf.length - 1; i >= 0; i--) {

      // Set up tracking for this entry
      initTrackingCode(trackerConf[i]);
    }

    return trackProxy;
  })();
};